// REAL-TIME harang — a BFF `GET /notifications/stream` SSE-jét fogyasztja.
//
// MIÉRT `fetch` ÉS NEM `EventSource`: az `EventSource` NEM tud `Authorization` fejlécet küldeni, a BFF pedig
// Bearer-token alapú (az access-token CSAK memóriában él — `lib/auth-store.ts`). Ezért `fetch` + `ReadableStream`
// olvasó, a Mo-widget streamelt-fetch mintája szerint (a `data:`-parser vendorozva: `components/mo/sse.ts`).
//
// AZ SSE-ÜZENET CSAK JELZÉS: a forrás-igazság a REST-feed. Jelzésre a feed-query-t INVALIDÁLJUK (nem a
// stream-payloadból építünk listát) — így nincs két adat-út, és a lapozás/olvasottság konzisztens marad.
//
// KÉT FRAME-TÍPUS jön ezen az EGY streamen (az Odin a `notify:{app}:{id}` ÉS a `perm:{id}` csatornát is relayeli):
//   • értesítés-jelzés → a feed-query invalidálása (a fenti szemantika),
//   • `{event:'permissions-changed'}` → a JOG-EPOCH push-ága: CSENDES token-refresh (`onPermissionsChanged`),
//     hogy a szerep/jog módosítása KIJELENTKEZTETÉS NÉLKÜL, azonnal érvényre jusson. A cache-eldobást NEM ez
//     végzi, hanem a token-store feliratkozása (`lib/permission-sync.ts`) — egy seam MINDEN refresh-útra.
//     Ez az „azonnaliság" ága; a FAL az Odin `pv`-guardja, ami az SSE-t megkerülő hívóra is hat.
//
// ÉLETCIKLUS:
//   • megnyitás: bejelentkezés után (van access-token) ÉS ha a tab LÁTHATÓ,
//   • bezárás: kijelentkezés · unmount · a tab elrejtése (`AbortController`) — a rejtett tab ne foglaljon slotot,
//   • újrakapcsolás: EXPONENCIÁLIS backoff jitterrel (1 → 2 → 4 → … max 30 s),
//   • 401: EGYSZERI csendes refresh (`tryRefresh`); siker → a token-változás újraindítja az effektet.
//
// A BACKOFF NULLÁZÁSA BIZONYÍTÉKHOZ KÖTÖTT (`markHealthy`): NEM a `res.ok` nullázza, hanem az első VALÓDI
// jelzés-frame vagy a `HEALTHY_STREAM_MS`-t túlélő kapcsolat. Ellenkező esetben egy azonnal záruló 200-as
// válasz (a régi BFF fail-soft alakja: 200 + egyetlen `error`-frame) másodperces fetch+refetch hurkot csinált.
//
// 429 (RATE-LIMIT / SLOT-KIMERÜLÉS): az Odin egy identityre MAX 10 PÁRHUZAMOS streamet engedélyez — több
// megnyitott tab ezt könnyen kimeríti. Ilyenkor NEM pörgünk: (1) HOSSZÚ (≥60 s) backoff, és (2) a REST-feed
// PERIODIKUS (60 s) frissítése a fallback, hogy a harang stream nélkül is friss maradjon (csak lassabban).
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { notificationKeys } from '../api';

import { parseSseData } from '@/components/mo/sse';
import { tryRefresh } from '@/lib/auth';
import { useAuthStore } from '@/lib/auth-store';

const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3100';

/** A normál újrakapcsolás backoff-plafonja. */
export const MAX_BACKOFF_MS = 30_000;
/** 429 → LEGALÁBB ennyit várunk (a slot-kimerülés nem oldódik meg másodpercek alatt). */
export const RATE_LIMITED_BACKOFF_MS = 60_000;
/** 429-fallback: a REST-feed periodikus frissítése, amíg nincs stream. */
export const FALLBACK_POLL_MS = 60_000;
/**
 * Ennyi ideig ÉLŐ stream = bizonyítottan működő csatorna (a backoff nullázható). MIÉRT kell külön időhöz kötni:
 * az Odin IDLE streamje csak SSE-KOMMENTET küld (`: ping` ~25 s-onként), ami nem `data:`-frame, tehát egy
 * teljesen egészséges, csendes streamen SOHA nem jön payload — a puszta „megnyílt a válasz" viszont NEM
 * bizonyíték (a BFF nyithat és rögtön zárhat is).
 */
export const HEALTHY_STREAM_MS = 30_000;

/** Exponenciális backoff JITTERREL (a fele fix, a fele random) — így több tab nem egyszerre próbál újra. */
function backoffMs(attempt: number): number {
  const capped = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
  return capped / 2 + Math.random() * (capped / 2);
}

/**
 * A MENET KÖZBENI hiba-frame felismerése (`{event:'error',statusCode}`) — a BFF ezzel jelez, ha a stream a
 * fejléc elküldése UTÁN szakadt meg (akkor már nem tud státuszt váltani). Visszatérés: a hiba status-kódja
 * (ismeretlen → 0), `null` ha ez egy NORMÁL jelzés-frame.
 */
function errorFrameStatus(payload: string): number | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return null; // nem JSON → jelzésként kezeljük (a payload tartalma egyébként sem érdekel)
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const frame = parsed as { event?: unknown; statusCode?: unknown };
  if (frame.event !== 'error') return null;
  return typeof frame.statusCode === 'number' ? frame.statusCode : 0;
}

/**
 * JOG-VÁLTOZÁS frame (`{event:'permissions-changed'}`) — az Odin a jog-epoch bumpjakor küldi ugyanezen a
 * csatornán (`perm:{identityId}`). Ez az EGYETLEN frame, aminek a TARTALMA számít: a többi jelzés-szemantikájú
 * (a REST a forrás-igazság), ez viszont MÁS teendőt jelent, mint egy értesítés (token-refresh, nem feed-refetch).
 *
 * A frame SOHA nem hordoz jog-információt (az Odin szándékosan üresen küldi) — a friss jogképet a REFRESHELT
 * TOKENBŐL kapjuk. Ez fontos: a csatornán nem utazik jogosultság, tehát nem is hamisítható vele jogkép.
 */
function isPermissionsChangedFrame(payload: string): boolean {
  try {
    const parsed: unknown = JSON.parse(payload);
    return (
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed as { event?: unknown }).event === 'permissions-changed'
    );
  } catch {
    return false;
  }
}

/** A tab látható-e (`document.hidden` tükre) — rejtett tabon nem tartunk nyitva streamet. */
function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || !document.hidden);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = (): void => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/**
 * A harang real-time csatornája. Nincs visszatérési értéke: a hatás a feed-query invalidálása (a badge/lista
 * onnan renderel). Bejelentkezés nélkül (nincs token) no-op.
 */
export function useNotificationStream(): void {
  const queryClient = useQueryClient();
  const token = useAuthStore((s) => s.token);
  const visible = useDocumentVisible();

  useEffect(() => {
    if (!token || !visible) return;

    const controller = new AbortController();
    let cancelled = false;
    let attempt = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;

    const invalidate = (): void => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    };
    /**
     * JOG-VÁLTOZÁS a szerver felől (az Odin jog-epoch PUSH ága) — a felhasználót NEM léptetjük ki, csak
     * CSENDBEN refreshelünk: az új token MÁR a friss `scope`/`permissions` claimekkel jön. (A fal nem ez,
     * hanem az Odin `pv`-guardja: refresh nélkül a régi token úgyis 401 `stale_permissions`-t kapna a
     * következő híváskor. Ez az ág csak az AZONNALISÁGOT adja.)
     *
     * A CACHE-ELDOBÁST SZÁNDÉKOSAN NEM ITT VÉGEZZÜK. A token három úton cserélődhet (401-retry, ez a push,
     * route-guard/reload), és ha az invalidálás bármelyik ágba lenne beépítve, a többi NÉMÁN kimaradna.
     * Ezért egyetlen seam van rá: a token-store feliratkozása (`lib/permission-sync.ts`), ami a JOGKÉP
     * tényleges változását nézi — így a puszta 15 perces lejárat-refresh NEM húz újra mindent.
     *
     * Ha a refresh nem sikerül (nincs session), NEM pörgetünk: a route-guard/logout kezeli.
     */
    const onPermissionsChanged = (): void => {
      void tryRefresh();
    };
    const startFallbackPolling = (): void => {
      pollTimer ??= setInterval(invalidate, FALLBACK_POLL_MS);
    };
    const stopFallbackPolling = (): void => {
      if (pollTimer !== undefined) {
        clearInterval(pollTimer);
        pollTimer = undefined;
      }
    };
    const scheduleRetry = (ms: number): void => {
      if (cancelled) return;
      retryTimer = setTimeout(() => void connect(), ms);
    };
    /** A csatorna BIZONYÍTOTTAN él → a backoff-lépcső és a poll-fallback visszaáll. */
    const markHealthy = (): void => {
      attempt = 0;
      stopFallbackPolling();
    };
    /**
     * Újrakapcsolás-ütemezés hiba után. A `status` a MEGÁLLAPÍTOTT hiba-kód (HTTP-státusz VAGY a menet közbeni
     * `error`-frame kódja), `null` ha nincs ilyen (a szerver rendben zárta a streamet). 429 → hosszú backoff +
     * REST-poll-fallback; minden más → normál (exponenciális) backoff. Az `attempt` MINDIG nő → nincs pörgés.
     */
    const retryAfterFailure = (status: number | null): void => {
      if (status === 429) {
        startFallbackPolling();
        scheduleRetry(Math.max(RATE_LIMITED_BACKOFF_MS, backoffMs(attempt++)));
        return;
      }
      scheduleRetry(backoffMs(attempt++));
    };

    const connect = async (): Promise<void> => {
      if (cancelled) return;

      let res: Response;
      try {
        res = await fetch(`${baseUrl.replace(/\/$/, '')}/notifications/stream`, {
          method: 'GET',
          credentials: 'include',
          headers: { authorization: `Bearer ${token}`, accept: 'text/event-stream' },
          signal: controller.signal,
        });
      } catch {
        // Hálózati hiba / abort — abortnál a cleanup már beállította a `cancelled`-et, így nem ütemezünk.
        retryAfterFailure(null);
        return;
      }
      if (cancelled) return;

      if (res.status === 401) {
        // Lejárt access-token → EGYSZERI csendes refresh. Siker esetén a store új tokent kap → az effekt
        // dependency-je változik → automatikus újrakapcsolás (itt nem ütemezünk, hogy ne duplázzunk).
        const refreshed = await tryRefresh();
        if (!refreshed) return; // nincs session → a route-guard/logout kezeli; ne pörgessük a szervert
        return;
      }
      if (!res.ok || !res.body) {
        // A BFF a stream INDULÁSA előtti hibát HTTP-státuszként adja (429 = per-identity stream-plafon,
        // 502/503 = Odin-oldali gond) → a status dönti el, mennyit várunk.
        retryAfterFailure(res.status);
        return;
      }

      // A válasz megnyílt — de az `attempt`-et itt MÉG NEM nullázzuk: egy rögtön záruló 200-as stream
      // (pl. hibás proxy) különben végtelen, másodperces újrakapcsolási hurokba vinne. A bizonyíték az ELSŐ
      // VALÓDI jelzés-frame, vagy a `HEALTHY_STREAM_MS`-nél hosszabb életű kapcsolat (lásd `markHealthy`).
      const openedAt = Date.now();
      let failureStatus: number | null = null;
      try {
        for await (const payload of parseSseData(res.body)) {
          if (cancelled) return;
          const errorStatus = errorFrameStatus(payload);
          if (errorStatus !== null) {
            // MENET KÖZBENI megszakadás: ez NEM jelzés → nincs invalidálás (különben a hiba-frame refetch-et
            // hajtana), és a status-kód szerinti ágon várunk.
            failureStatus = errorStatus;
            break;
          }
          // A csatorna él → a backoff nullázható, akármelyik frame-típus jött.
          markHealthy();
          if (isPermissionsChangedFrame(payload)) {
            // JOG-VÁLTOZÁS: token-refresh + teljes invalidálás. NEM feed-refetch (nem értesítés érkezett).
            onPermissionsChanged();
            continue;
          }
          // Egyébként: a payload tartalmát SZÁNDÉKOSAN nem használjuk (JELZÉS-szemantika) — a REST-feed a
          // forrás-igazság, abból renderel a badge/lista.
          invalidate();
        }
      } catch {
        // A stream olvasása megszakadt (hálózat/abort) → az alábbi újrakapcsolás kezeli.
      }
      if (cancelled) return;
      // Sokáig élt, hiba-frame nélkül záruló stream = szintén működő csatorna (csendes, `data:`-frame nélküli
      // stream is lehet teljesen egészséges — lásd `HEALTHY_STREAM_MS`).
      if (failureStatus === null && Date.now() - openedAt >= HEALTHY_STREAM_MS) markHealthy();
      retryAfterFailure(failureStatus);
    };

    void connect();

    return () => {
      cancelled = true;
      controller.abort();
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      stopFallbackPolling();
    };
  }, [token, visible, queryClient]);
}
